import Image from "next/image";
import { cva, cx } from "cva";
import { Body } from "~/ui-library";

export interface TestimonialSimpleCenteredProps {
  appearance: "brand" | "dark" | "light";
  quote: string;
  personsName: string;
  personsTitle?: string;
  personImageUrl: string;
  personImageAlt: string;
  logoImageUrl: string;
  logoImageAlt: string;
}

const blockStyle = cva(
  ["relative isolate overflow-hidden px-6 py-4 sm:py-6 lg:p-8"],
  {
    defaultVariants: {
      appearance: "light",
    },
    variants: {
      appearance: {
        brand: "bg-brand-indigo",
        dark: "bg-gray-900",
        light: "bg-white",
      },
    },
  }
);

const gradientBackgroundOne = cva(["absolute inset-0 -z-10 opacity-20"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand:
        "bg-[radial-gradient(45rem_50rem_at_top,theme(colors.gray.100),colors.brand.indigo)]",
      dark: "bg-[radial-gradient(45rem_50rem_at_top,theme(colors.brand.indigo.700),colors.gray.900)]",
      light:
        "bg-[radial-gradient(45rem_50rem_at_top,theme(colors.brand.indigo.100),white)]",
    },
  },
});

const gradientBackgroundTwo = cva(
  [
    "absolute inset-y-0 right-1/2 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-30deg] shadow-3 ring-1 sm:mr-28 lg:mr-0 xl:mr-16 xl:origin-center",
  ],
  {
    defaultVariants: {
      appearance: "light",
    },
    variants: {
      appearance: {
        brand:
          "bg-brand-indigo-700 shadow-brand-indigo-100/10 ring-brand-indigo-600",
        dark: "bg-gray-900 shadow-brand-indigo-200/10 ring-brand-indigo-800",
        light: "bg-white shadow-brand-indigo-600/10 ring-brand-indigo-50",
      },
    },
  }
);

const textColor = cva([], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "text-white",
      dark: "text-white",
      light: "text-gray-900",
    },
  },
});

const svgColor = cva([], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "fill-white",
      dark: "fill-white",
      light: "fill-gray-900",
    },
  },
});

export const TestimonialSimpleCentered = ({
  appearance,
  quote,
  personImageUrl,
  personImageAlt,
  personsName,
  personsTitle,
  logoImageAlt,
  logoImageUrl,
}: TestimonialSimpleCenteredProps): JSX.Element => (
  <section className={blockStyle({ appearance })}>
    <div className={gradientBackgroundOne({ appearance })} />
    <div className={gradientBackgroundTwo({ appearance })} />
    <div className="mx-auto max-w-2xl lg:max-w-4xl">
      <div className="relative mx-auto h-12 max-w-[200px]">
        <Image
          fill
          style={{ objectFit: "contain" }}
          src={logoImageUrl}
          alt={logoImageAlt}
        />
      </div>
      <figure className="mt-10">
        <blockquote className={cx("text-center ", textColor({ appearance }))}>
          <Body bold size="large" className={textColor({ appearance })}>
            {quote}
          </Body>
        </blockquote>
        <figcaption className="mt-10">
          <Image
            className="mx-auto size-10 rounded-full"
            width={44}
            height={44}
            src={personImageUrl}
            alt={personImageAlt}
          />
          <div className="mt-4 flex items-center justify-center space-x-3">
            <Body as="div" bold className={textColor({ appearance })}>
              {personsName}
            </Body>
            {personsTitle && (
              <>
                <svg
                  viewBox="0 0 2 2"
                  width={3}
                  height={3}
                  aria-hidden="true"
                  className={svgColor({ appearance })}
                >
                  <circle cx={1} cy={1} r={1} />
                </svg>
                <Body as="div" className={textColor({ appearance })}>
                  {personsTitle}
                </Body>
              </>
            )}
          </div>
        </figcaption>
      </figure>
    </div>
  </section>
);
